/**
 * school_year가 "2025" / "2025-2026"처럼 섞여 있어도
 * 앞자리 연도 기준으로 안전하게 비교·초기선택하기 위한 유틸.
 */

export function getSchoolYearSortValue(schoolYear) {
  const match = String(schoolYear ?? "").match(/^(\d+)/);
  return match ? Number(match[1]) : Number.NEGATIVE_INFINITY;
}

/** 내림차순(최신 학년도 우선). 연도 같으면 전체 문자열 localeCompare. */
export function compareSchoolYearDesc(aSchoolYear, bSchoolYear) {
  const diff =
    getSchoolYearSortValue(bSchoolYear) - getSchoolYearSortValue(aSchoolYear);
  if (diff !== 0) return diff;
  return String(bSchoolYear ?? "").localeCompare(String(aSchoolYear ?? ""));
}

export function compareSchoolYearAsc(aSchoolYear, bSchoolYear) {
  return -compareSchoolYearDesc(aSchoolYear, bSchoolYear);
}

/** DropdownSchoolYear와 동일한 루트 학년도 행 */
export function getSchoolYearRootRows(settingData) {
  if (!Array.isArray(settingData) || settingData.length === 0) return [];

  const roots = settingData.filter(
    (r) =>
      String(r?.hierarchy_level) === "0" &&
      (r?.parent_id === 0 ||
        r?.parent_id === "0" ||
        r?.parent_id == null ||
        r?.parent_id === "")
  );

  // 예전 데이터에 hierarchy_level 표기가 깨진 경우 fallback
  if (roots.length > 0) return roots;

  const byYear = new Map();
  settingData.forEach((row) => {
    const key = String(row?.school_year ?? "");
    if (!key || byYear.has(key)) return;
    byYear.set(key, row);
  });
  return Array.from(byYear.values());
}

function toDateKey(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function getTodayDateKey() {
  // setTermIdByToday와 동일하게 UTC YYYY-MM-DD 사용
  return new Date().toISOString().slice(0, 10);
}

/**
 * schoolYearPeriod / termPeriod 공통: school_year_id + start_date + end_date
 * 오늘이 포함되는 school_year_id를 반환. 없으면 null.
 */
export function findSchoolYearIdContainingToday(periodData, today = getTodayDateKey()) {
  if (!Array.isArray(periodData) || periodData.length === 0) return null;

  const todayKey = toDateKey(today) || getTodayDateKey();
  const hit = periodData.find((row) => {
    const start = toDateKey(row?.start_date);
    const end = toDateKey(row?.end_date);
    if (!start || !end || row?.school_year_id == null) return false;
    return start <= todayKey && todayKey <= end;
  });

  return hit ? hit.school_year_id : null;
}

/**
 * 초기 학년도 선택:
 * 1) periodData에 오늘이 포함되면 그 학년도
 * 2) 없으면 통일된 내림차순 정렬의 맨 위
 *
 * @returns {{ schoolYear: string, schoolYearId: number|string, row: object } | null}
 */
export function findInitialSchoolYear(settingData, { periodData } = {}) {
  const roots = getSchoolYearRootRows(settingData);
  if (roots.length === 0) return null;

  const sorted = [...roots].sort((a, b) =>
    compareSchoolYearDesc(a.school_year, b.school_year)
  );

  const todaySchoolYearId = findSchoolYearIdContainingToday(periodData);
  if (todaySchoolYearId != null) {
    const current = sorted.find(
      (r) => String(r.id) === String(todaySchoolYearId)
    );
    if (current) {
      return {
        schoolYear: current.school_year,
        schoolYearId: current.id,
        row: current,
      };
    }
  }

  const top = sorted[0];
  return {
    schoolYear: top.school_year,
    schoolYearId: top.id,
    row: top,
  };
}
